<script lang="ts">
  import ChatHeader from '../chat/ChatHeader.svelte';
  import ChatInput from '../chat/ChatInput.svelte';
  import ChatView from '../chat/ChatView.svelte';
  import DropZone from '../common/DropZone.svelte';
  import { getRoute, navigate } from '../../route.svelte';
  import {
    createChat,
    pendingInitial,
    MAX_CHAT_MESSAGE_DOCUMENTS,
    loadAIPreferences,
    getEffortOptions,
    defaultEffort,
  } from '../../chats.svelte';
  import { currentUser } from '../../auth.svelte';
  import { MAIN_URL } from '../../externalUrls';
  import { showGlobalError } from '../../errorBanner.svelte';

  let { onToggleSidebar, palette, isSidebarCollapsed, isMobile } = $props();

  let route = $derived(getRoute());
  let startingFeature = $state(false);

  // ── Nivel de análisis en la pantalla de inicio ───────────────
  // Sin chat todavía: la elección viaja en el POST de creación, así el primer
  // turno ya usa el nivel elegido. El default lo define el admin de Django.
  let homeEffortChoice = $state<string | null>(null);
  let homeEffort = $derived(homeEffortChoice ?? defaultEffort.value);
  let effortOptions = $derived(getEffortOptions());

  $effect(() => {
    void loadAIPreferences();
  });

  const features = [
    {
      icon: 'bi-file-earmark-check',
      title: 'Revisar un documento',
      subtitle: 'Contratos, tutelas, demandas y acuerdos',
      tag: 'Análisis profundo',
      prompt: 'Quiero revisar y calificar un documento legal que voy a subir. Ayúdame a iniciar la revisión especializada.',
    },
    {
      icon: 'bi-graph-up-arrow',
      title: 'Predecir un caso',
      subtitle: 'Probabilidad de éxito y estrategia jurídica',
      tag: 'Análisis profundo',
      prompt: 'Quiero iniciar un análisis predictivo de un caso y conocer mi probabilidad de éxito. Guíame para contarte los hechos.',
    },
    {
      icon: 'bi-car-front',
      title: 'Consultar un vehículo',
      subtitle: 'Multas, SOAT, tecnomecánica y taxis',
      tag: 'Consulta especializada',
      prompt: 'Quiero consultar multas, SOAT o tecnomecánica de un vehículo. Dime qué dato necesitas para iniciar.',
    },
    {
      icon: 'bi-bank',
      title: 'Buscar procesos',
      subtitle: 'Consulta en Rama Judicial por nombre',
      prompt: 'Quiero consultar procesos judiciales. Dime qué información necesitas para hacer la búsqueda.',
    },
    {
      icon: 'bi-house-check',
      title: 'Consultar propiedades',
      subtitle: 'Predios registrados en Supernotariado',
      prompt: 'Quiero consultar las propiedades o predios registrados a nombre de una persona. Dime qué información necesitas.',
    },
    {
      icon: 'bi-search-heart',
      title: 'Investigar un deudor',
      subtitle: 'Procesos, inmuebles y bienes embargables',
      prompt: 'Necesito investigar los bienes y procesos de un deudor para definir una estrategia de cobro. Dime qué datos necesitas.',
    },
    {
      icon: 'bi-pencil-square',
      title: 'Redactar un documento',
      subtitle: 'Tutela, petición, contrato o demanda',
      prompt: 'Quiero redactar un documento legal profesional. Ayúdame a identificar el documento adecuado y los datos necesarios.',
    },
    {
      icon: 'bi-chat-square-text',
      title: 'Resolver una consulta',
      subtitle: 'Respuesta jurídica con normas y fuentes',
      prompt: 'Tengo una consulta legal y quiero una respuesta con fundamento normativo y fuentes verificadas.',
    },
  ];

  // ── Handlers ─────────────────────────────────────────────

  async function handleSendFromHome(text: string) {
    try {
      const { id } = await createChat(text, homeEffortChoice);
      navigate(`/chat/${id}`);
    } catch (e) {
      console.error(e);
      showGlobalError('No pudimos crear el chat. Intenta de nuevo.');
    }
  }

  async function handleSendAudioFromHome(audioData: string, mimeType: string) {
    try {
      const { id } = await createChat('🎙️ Mensaje de voz', homeEffortChoice);
      pendingInitial[id] = {
        text: '🎙️ Mensaje de voz',
        attachmentType: 'audio',
        mediaData: audioData,
        mimeType,
      };
      navigate(`/chat/${id}`);
    } catch (e) {
      console.error(e);
      showGlobalError('No pudimos crear el chat. Intenta de nuevo.');
    }
  }

  async function handleSendFilesFromHome(files: File[], caption: string) {
    if (files.length > MAX_CHAT_MESSAGE_DOCUMENTS) {
      showGlobalError(
        `Puedes adjuntar máximo ${MAX_CHAT_MESSAGE_DOCUMENTS} documentos por mensaje. ` +
        'Para cargas masivas, abre un chat y usa la Base documental.'
      );
      return;
    }
    try {
      const fallbackTitle = files.map(file => file.name).join(', ');
      const { id } = await createChat(caption || fallbackTitle, homeEffortChoice);
      pendingInitial[id] = {
        text: caption || fallbackTitle,
        attachmentType: 'file',
        files,
      };
      navigate(`/chat/${id}`);
    } catch (e) {
      console.error(e);
      showGlobalError('No pudimos crear el chat. Intenta de nuevo.');
    }
  }

  async function handleSuggestionClick(text: string) {
    if (startingFeature) return;
    startingFeature = true;
    try {
      await handleSendFromHome(text);
    } finally {
      startingFeature = false;
    }
  }
</script>

<div class="chat-layout">
  <ChatHeader
    {onToggleSidebar}
    {palette}
    {isSidebarCollapsed}
    {isMobile}
  />

  {#if route.name === 'home'}
    <DropZone
      onFiles={(files) => handleSendFilesFromHome(files, '')}
      {palette}
    >
      <div class="chat-body scroll-y">
        <div class="welcome-container">
        <a class="welcome-logo-link" href={MAIN_URL} aria-label="Ir a Jurismart">
          <img class="welcome-logo" src={palette.mainLogoSrc} alt="Jurismart" />
        </a>
        <div class="welcome-text-block">
          <h1 class="welcome-title" style="color: {palette.textPrimary};">
            ¿Qué necesitas resolver hoy?
          </h1>
          <p class="welcome-text">
            Elige una capacidad de Jurismart o escribe directamente tu consulta.
          </p>
        </div>
      </div>

      <div class="feature-grid" aria-label="Capacidades de Jurismart">
        {#each features as feature}
          <button
            class="feature-card"
            type="button"
            onclick={() => handleSuggestionClick(feature.prompt)}
            disabled={startingFeature}
            style={`background-color: ${palette.bgChatInput}; border-color: ${palette.borderSoft}; color: ${palette.textPrimary};`}
          >
            <span class="feature-icon" style={`color: ${palette.accent}; background: color-mix(in srgb, ${palette.accent} 10%, transparent);`}>
              <i class="bi {feature.icon}"></i>
            </span>
            <span class="feature-content">
              <span class="feature-heading">
                <span class="feature-title">{feature.title}</span>
                {#if feature.tag}
                  <span class="feature-tag" style={`color: ${palette.accent};`}>{feature.tag}</span>
                {/if}
              </span>
              <span class="feature-subtitle" style={`color: ${palette.textSecondary};`}>
                {feature.subtitle}
              </span>
            </span>
            <i class="bi bi-chevron-right feature-arrow" style={`color: ${palette.textSecondary};`}></i>
          </button>
        {/each}
      </div>
    </div>

    <div class="input-area">
      <ChatInput
        {palette}
        effort={homeEffort}
        {effortOptions}
        onEffortChange={(value) => (homeEffortChoice = value)}
        onSend={handleSendFromHome}
        onSendAudio={handleSendAudioFromHome}
        onSendFiles={handleSendFilesFromHome}
      />
      <p class="disclaimer">
        Recuerda que la IA puede cometer errores. Te recomendamos verificar la
        información con las referencias citadas.
      </p>
    </div>
    </DropZone>
  {:else if route.name === 'chat'}
    <!-- Vista de chat -->
    <ChatView chatId={route.id} {palette} />
  {/if}
</div>

<style>
  /* reutiliza tus estilos anteriores para .chat-layout, .chat-body, etc.
     Añade solo lo necesario si cambió algo */

  .chat-layout {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .chat-body {
    flex: 1;
    padding: 0 32px 16px 32px;
    min-width: 0;
    min-height: 0;
    width: 100%;
    overflow-x: hidden;
  }

  .welcome-container {
    max-width: 900px;
    margin: 8px auto 0 auto;
    text-align: center;
    padding: 0 12px;
  }

  .welcome-logo {
    max-width: 220px;
    width: 100%;
    margin-bottom: 12px;
  }

  .welcome-logo-link {
    display: inline-block;
  }

  .welcome-text-block {
    max-width: 650px;
    margin: 0 auto;
  }

  .welcome-text {
    margin: 0;
    font-size: 0.82rem;
  }

  .welcome-title {
    margin: 0 0 4px;
    font-size: 1.25rem;
    line-height: 1.25;
  }

  .feature-grid {
    width: 100%;
    max-width: 820px;
    margin: 20px auto 8px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .feature-card {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid;
    border-radius: 12px;
    padding: 10px 11px;
    text-align: left;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  }

  .feature-card:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 5px 16px rgba(0, 0, 0, 0.07);
  }

  .feature-card:disabled {
    opacity: 0.55;
    cursor: wait;
  }

  .feature-icon {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    display: grid;
    place-items: center;
    border-radius: 9px;
    font-size: 15px;
  }

  .feature-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .feature-heading {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .feature-title {
    font-size: 12.5px;
    font-weight: 650;
  }

  .feature-tag {
    padding: 1px 5px;
    border: 1px solid currentColor;
    border-radius: 999px;
    font-size: 8px;
    font-weight: 700;
    line-height: 1.4;
    white-space: nowrap;
    opacity: 0.75;
  }

  .feature-subtitle {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 10.5px;
  }

  .feature-arrow {
    flex: 0 0 auto;
    font-size: 10px;
    opacity: 0.55;
  }

  .input-area {
    padding: 0 32px 20px 32px;
    width: 100%;
    min-width: 0;
    flex: 0 0 auto;
  }

  .disclaimer {
    max-width: 900px;
    margin: 8px auto 0 auto;
    font-size: 11px;
    line-height: 1.4;
    opacity: 0.85;
    text-align: center;
  }

  @media (max-width: 900px) {
    .chat-body {
      padding: 0 16px 12px 16px;
    }
    .input-area {
      padding: 0 16px 16px 16px;
    }
    .welcome-logo {
      max-width: 200px;
    }
  }

  @media (max-width: 600px) {
    .chat-body {
      padding: 0 8px 8px 8px;
    }
    .input-area {
      padding: 0 8px max(12px, env(safe-area-inset-bottom)) 8px;
    }
    .welcome-container {
      margin-top: 2px;
    }

    .welcome-logo {
      max-width: 165px;
      margin-bottom: 7px;
    }

    .welcome-title {
      font-size: 1.05rem;
    }

    .feature-grid {
      grid-template-columns: 1fr;
      margin-top: 14px;
      gap: 6px;
    }

    .feature-card {
      padding: 8px 9px;
    }

    .feature-icon {
      width: 31px;
      height: 31px;
      flex-basis: 31px;
    }

    .feature-tag {
      display: none;
    }
  }
</style>
